import { Divider, Loading } from '@affine/component';
import { Button } from '@affine/component/ui/button';
import { useGuard } from '@affine/core/components/guard';
import {
  useConnectivityStatus,
  useUiOfflineSignal,
} from '@affine/core/components/hooks/use-connectivity-status';
import { DocService } from '@affine/core/modules/doc';
import * as reviewStyles from '@affine/core/modules/permissions/views/permission-review-list.css';
import { ShareInfoService } from '@affine/core/modules/share-doc';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { CloudSvg } from '../cloud-svg';
import { CopyLinkButton } from './copy-link-button';
import { MembersPermission, PublicDoc } from './general-access';
import * as styles from './index.css';
import { InviteInput } from './invite-member-editor';
import { MembersRow } from './member-management';
import type { ShareMenuProps } from './share-menu';
import { useDocShareRestrictions } from './use-doc-share-restrictions';

export const LocalSharePage = (props: ShareMenuProps) => {
  const t = useI18n();
  const {
    workspaceMetadata: { id: workspaceId },
  } = props;
  return (
    <>
      <div className={styles.localSharePage}>
        <div className={styles.columnContainerStyle} style={{ gap: '12px' }}>
          <div
            className={styles.descriptionStyle}
            style={{ maxWidth: '230px' }}
          >
            {t['com.streamlain.share-menu.EnableCloudDescription']()}
          </div>
          <div>
            <Button
              onClick={props.onEnableAffineCloud}
              variant="primary"
              data-testid="share-menu-enable-affine-cloud-button"
            >
              {t['Enable AFFiNE Cloud']()}
            </Button>
          </div>
        </div>
        <div className={styles.cloudSvgContainer}>
          <CloudSvg />
        </div>
      </div>
      <CopyLinkButton workspaceId={workspaceId} secondary />
    </>
  );
};

export const AFFiNESharePage = (
  props: ShareMenuProps & {
    hideMemberControls?: boolean;
    onClickInvite?: () => void;
    onClickMembers?: () => void;
  }
) => {
  const t = useI18n();
  const {
    workspaceMetadata: { id: workspaceId },
  } = props;
  const shareInfoService = useService(ShareInfoService);
  const docService = useService(DocService);
  const connectivityStatus = useConnectivityStatus();
  const isOffline = useUiOfflineSignal(connectivityStatus.offline);
  const {
    isReady: isFolderCacheReady,
    privateFolderDocShareBlocked,
    timedOut,
    retry: retryRestrictions,
  } = useDocShareRestrictions();

  const canManageUsers = useGuard('Doc_Users_Manage', docService.doc.id);

  const canPublish = useGuard('Doc_Publish', docService.doc.id);

  const info = useLiveData(shareInfoService.shareInfo.info$);
  const error = useLiveData(shareInfoService.shareInfo.error$);
  const refreshing = useLiveData(shareInfoService.shareInfo.isRevalidating$);
  const mutating = useLiveData(shareInfoService.shareInfo.isMutating$);
  const unavailable = isOffline || Boolean(error) || timedOut;
  const loading = !info && !unavailable;
  const locked = unavailable || refreshing || mutating || !isFolderCacheReady;
  const retry = () => {
    shareInfoService.shareInfo.revalidate();
    if (!isFolderCacheReady) retryRestrictions();
  };

  return (
    <div className={styles.content}>
      {unavailable ? (
        <ShareLoadNotice
          offline={isOffline}
          onRetry={retry}
          loading={refreshing}
        />
      ) : null}
      {loading || refreshing ? (
        <div className={reviewStyles.notice} role="status">
          <Loading size={14} />
          {t['com.streamlain.share-menu.public-sharing.loading']()}
        </div>
      ) : null}
      {info ? (
        <div className={styles.columnContainerStyle}>
          <div className={styles.memberRowsStyle}>
            {privateFolderDocShareBlocked && (
              <div className={styles.privateFolderWarning}>
                {t[
                  'com.streamlain.share-menu.invite-editor.private-folder-doc-disabled'
                ]()}
              </div>
            )}
            {!props.hideMemberControls &&
              canManageUsers &&
              props.onClickInvite && (
                <InviteInput
                  onFocus={props.onClickInvite}
                  disabled={locked || privateFolderDocShareBlocked}
                />
              )}
            {!props.hideMemberControls && props.onClickMembers ? (
              <MembersRow onClick={props.onClickMembers} />
            ) : null}
          </div>

          <div className={styles.generalAccessStyle}>
            {t['com.streamlain.share-menu.generalAccess']()}
          </div>
          <MembersPermission
            disabled={locked || !canManageUsers || privateFolderDocShareBlocked}
          />
          <PublicDoc disabled={locked || !canPublish} />
          {info.publicSharing?.public ? (
            <CopyLinkButton
              workspaceId={workspaceId}
              publicLink
              disabled={locked}
            />
          ) : null}
        </div>
      ) : null}
      <Divider className={styles.divider} />
      <CopyLinkButton workspaceId={workspaceId} secondary />
      <div className={reviewStyles.notice}>
        {t['com.streamlain.share-menu.public-sharing.internal-note']()}
      </div>
    </div>
  );
};

export const SharePage = (
  props: ShareMenuProps & {
    hideMemberControls?: boolean;
    onClickInvite?: () => void;
    onClickMembers?: () => void;
  }
) => {
  if (props.workspaceMetadata.flavour === 'local') {
    return <LocalSharePage {...props} />;
  } else {
    return (
      // TODO(@eyhn): refactor this part
      <ErrorBoundary
        fallbackRender={({ resetErrorBoundary }) => (
          <ShareLoadNotice onRetry={resetErrorBoundary} />
        )}
        onError={error => {
          console.error('[ShareMenu] Share page render failed:', error);
        }}
      >
        <Suspense fallback={<Loading size={16} />}>
          <AFFiNESharePage {...props} />
        </Suspense>
      </ErrorBoundary>
    );
  }
};

export function ShareLoadNotice({
  offline = false,
  loading = false,
  onRetry,
}: {
  offline?: boolean;
  loading?: boolean;
  onRetry(): void;
}) {
  const t = useI18n();
  return (
    <div className={reviewStyles.notice} role={offline ? 'status' : 'alert'}>
      <span>
        {t[
          offline
            ? 'com.streamlain.permissions.review.offline'
            : 'com.streamlain.share-menu.public-sharing.error'
        ]()}
      </span>
      <Button variant="plain" disabled={loading || offline} onClick={onRetry}>
        {t['com.streamlain.permissions.review.retry']()}
      </Button>
    </div>
  );
}
